import * as pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";
import dayjs from "dayjs";
import { fetchImageAsBase64 } from "../../../../../../../utils/imageUtils";

//APP
import { imgLogo } from "../../../../../../../utils/base64-Imgs";
import { typeEstruturaComparativoInsumos } from "./index";
import { numeroMilhar } from "../../../../../../../utils";

(pdfMake as any).vfs = pdfFonts.pdfMake.vfs;

export interface HeaderDef {
    logo?: string;
    titulo: string;
    razaoSocial: string;
    cliente?: string;
    autor?: string;
}

export const gerarDocDefinition = (
    dados: typeEstruturaComparativoInsumos[],
    headerInfo?: HeaderDef
) => {
    const content: any[] = [];

    // Header Principal (Somente Primeira Página)
    content.push({
        stack: [
            {
                image: headerInfo?.logo || 'imgLogoAlias',
                width: 80,
                alignment: 'center',
                margin: [0, 0, 0, 10]
            },
            {
                text: `${headerInfo?.titulo}`,
                style: 'headerMain',
                alignment: 'center',
                margin: [0, 0, 0, 10]
            },
            {
                canvas: [
                    {
                        type: 'rect',
                        x: 0,
                        y: 0,
                        w: 760,
                        h: 4,
                        linearGradient: ['#e5f1e1', '#5da57d', '#0e743f']
                    }
                ],
            },
            {
                stack: [
                    {
                        text: `${headerInfo?.razaoSocial}`,
                        fontSize: 8,
                        bold: true,
                        color: '#4D4D4D',
                        alignment: 'center'
                    },
                    ...(headerInfo?.cliente ? [{
                        text: `Cliente: ${headerInfo.cliente}`,
                        fontSize: 13,
                        bold: true,
                        color: '#000000',
                        alignment: 'center',
                        margin: [0, 10, 0, 0]
                    }] : [])
                ],
                margin: [0, 5, 0, 20]
            }
        ],
        margin: [0, 0, 0, 10]
    });

    dados.forEach((propriedade) => {
        // Header Propriedade
        content.push({
            table: {
                widths: ['*'],
                body: [
                    [
                        {
                            text: propriedade.nome_propriedade || "PROPRIEDADE NÃO INFORMADA",
                            style: 'headerPropriedade',
                            alignment: 'center',
                            fillColor: '#5da57d',
                            color: 'white',
                            bold: true,
                            margin: [0, 5, 0, 5]
                        }
                    ]
                ]
            },
            layout: 'noBorders',
            margin: [0, 0, 0, 0]
        });

        propriedade.lista_insumos_por_talhao.forEach((talhao) => {
            // Header Talhao
            content.push({
                table: {
                    widths: ['*'],
                    body: [
                        [
                            {
                                text: `${talhao.nome_talhao || "TALHÃO NÃO INFORMADO"} (${talhao.area || 0} ha)`,
                                style: 'headerTalhao',
                                alignment: 'center',
                                fillColor: '#fff',
                                color: '#165c13',
                                bold: true,
                                margin: [2, 2, 2, 2],
                                border: [true, true, true, true]
                            }
                        ]
                    ]
                },
                layout: {
                    hLineWidth: () => 1,
                    vLineWidth: () => 1,
                    hLineColor: () => '#165c13',
                    vLineColor: () => '#165c13'
                },
                margin: [0, 0, 0, 0]
            });

            let globalInsumoIndex = 0; // Para controle de zebra por bloco lógico
            const corHeader = '#0e743f';
            const bodyInsumos: any[] = [
                // Table Header Insumos
                [
                    { text: 'Insumo', style: 'tableHeader', alignment: 'left', fillColor: corHeader, color: 'white' },
                    { text: 'Tipo', style: 'tableHeader', alignment: 'left', fillColor: corHeader, color: 'white' },
                    { text: 'Qtd. Planejada', style: 'tableHeader', alignment: 'left', fillColor: corHeader, color: 'white' },
                    { text: 'Qtd. Executada', style: 'tableHeader', alignment: 'left', fillColor: corHeader, color: 'white' },
                    { text: 'Desvio', style: 'tableHeader', alignment: 'left', fillColor: corHeader, color: 'white' },
                    { text: 'Uso', style: 'tableHeader', alignment: 'left', fillColor: corHeader, color: 'white' },
                    { text: 'Precisão', style: 'tableHeader', alignment: 'left', fillColor: corHeader, color: 'white' }
                ]
            ];

            talhao.lista_insumos.forEach((insu) => {

                const zebraColor = (globalInsumoIndex % 2 === 0) ? null : '#e5f1e1';

                const desvioCalculado = (insu.quantidade_executada || 0) - (insu.quantidade_planejada || 0);

                // Handling exact display format
                // In image: desvio is absolute value but if formula is (planejada - executada), we can just print it. Wait, the user said `Desvio -> (quantidade_planejada - quantidade_executada)`. I'll format as number.
                const desvioFormatado = Number(desvioCalculado) === 0 ? '-' : `${numeroMilhar(desvioCalculado)} ${insu.medida || ''}`.trim();
                const usoFormatado = `${insu.porcentagem || 0}%`.replace(/\s/g, '');
                const precisaoFormatado = `${insu.precisao || 0}%`.replace(/\s/g, '');
                const qtdPlanejadaFormatado = Number(insu.quantidade_planejada || 0) === 0 ? '-' : `${numeroMilhar(insu.quantidade_planejada)} ${insu.medida || ''}`.trim();
                const qtdExecutadaFormatado = Number(insu.quantidade_executada || 0) === 0 ? '-' : `${numeroMilhar(insu.quantidade_executada)} ${insu.medida || ''}`.trim();

                let corPrecisao = zebraColor;
                let corTextoPrecisao = 'black';
                const p = insu.precisao || 0;
                if (p === 100) {
                    corPrecisao = '#eefdde'; // Verde
                    corTextoPrecisao = '#55c51e';
                } else if (p < 100 && p >= 95) {
                    corPrecisao = '#dfecf7'; // Azul
                    corTextoPrecisao = '#0857d8';
                } else if (p < 95 && p >= 80) {
                    corPrecisao = '#FFF7CC'; // Amarelo
                    corTextoPrecisao = 'black';
                } else if (p < 80) {
                    corPrecisao = '#ffe7e7'; // Vermelho
                    corTextoPrecisao = '#d60024';
                }

                bodyInsumos.push([
                    { text: `${insu.insumo_nome} ${insu.tipo_nome === 'Biológico' ? ` (${insu.tipo_fisico})` : ''}`, margin: [2, 4, 2, 4], alignment: 'left', fillColor: zebraColor },
                    { text: insu.tipo_nome || '-', margin: [2, 4, 2, 4], alignment: 'left', fillColor: zebraColor },
                    { text: qtdPlanejadaFormatado, margin: [2, 4, 2, 4], alignment: 'left', fillColor: zebraColor },
                    { text: qtdExecutadaFormatado, margin: [2, 4, 2, 4], alignment: 'left', fillColor: zebraColor },
                    { text: desvioFormatado, margin: [2, 4, 2, 4], alignment: 'left', fillColor: zebraColor },
                    { text: usoFormatado, margin: [2, 4, 2, 4], alignment: 'left', fillColor: zebraColor },
                    { text: precisaoFormatado, margin: [2, 4, 2, 4], alignment: 'left', fillColor: corPrecisao, color: corTextoPrecisao, bold: true },
                ]);

                globalInsumoIndex++;
            });

            if (talhao.lista_insumos.length === 0) {
                bodyInsumos.push([
                    { text: 'Nenhum insumo planejado para este talhão.', colSpan: 7, margin: [2, 4, 2, 4], alignment: 'center', italics: true, fillColor: null },
                    {}, {}, {}, {}, {}, {}
                ]);
            }

            content.push({
                table: {
                    widths: ['34%', '11%', '11%', '11%', '11%', '11%', '11%'],
                    body: bodyInsumos
                },
                layout: {
                    fillColor: function (rowIndex: number, node: any, columnIndex: number) {
                        if (rowIndex === 0) return '#a6a6a6'; // Header principal
                        return null;
                    }
                },
                margin: [0, 0, 0, 15]
            });
        });
    });

    return {
        content: content,
        pageOrientation: 'landscape',
        footer: (currentPage: number, pageCount: number) => {
            const dataGeracao = dayjs().format("DD/MM/YYYY, HH:mm:ss");
            return {
                stack: [
                    { text: '', margin: [0, 5, 0, 0] },
                    {
                        canvas: [
                            {
                                type: 'rect',
                                x: 0,
                                y: 0,
                                w: 760,
                                h: 4,
                                linearGradient: ['#e5f1e1', '#5da57d', '#0e743f']
                            }
                        ],
                        margin: [0, 0, 0, 5]
                    },
                    { text: '', margin: [0, 5, 0, 0] },
                    {
                        columns: [
                            {
                                width: '*',
                                stack: [
                                    {
                                        image: 'imgLogoAlias',
                                        width: 50,
                                        margin: [0, 3, 0, 0],
                                    },
                                ],
                                alignment: 'left',
                            },
                            {
                                width: '*',
                                stack: [
                                    {
                                        text: `Gerado por: ${headerInfo?.autor}`,
                                        bold: true,
                                        alignment: 'center',
                                        fontSize: 7,
                                        color: '#4D4D4D'
                                    },
                                    {
                                        text: `${dataGeracao}`,
                                        alignment: 'center',
                                        fontSize: 7,
                                        color: '#4D4D4D'
                                    },
                                ],
                                margin: [0, 3, 0, 0],
                                alignment: 'center',
                            },
                            {
                                width: '*',
                                text: `${currentPage} de ${pageCount}`,
                                alignment: 'right',
                                fontSize: 8,
                                margin: [0, 3, 0, 0],
                                color: '#4D4D4D'
                            },
                        ],
                    },
                    {
                        width: '*',
                        text: `Licenciado para: Agromarques - Consultoria Agronômica e Agricultura de Precisão 24.901.637/0001-17`,
                        alignment: 'left',
                        fontSize: 6,
                        margin: [0, 3, 0, 10],
                        color: '#4D4D4D'
                    },
                ],
                margin: [40, 0, 40, 0],
            };
        },
        images: {
            imgLogoAlias: imgLogo
        },
        styles: {
            headerMain: {
                fontSize: 20,
                bold: true,
                color: 'black'
            },
            headerPropriedade: {
                fontSize: 14,
                bold: true
            },
            headerTalhao: {
                fontSize: 12,
                bold: true
            },
            tableHeader: {
                bold: true,
                fontSize: 11,
                color: 'white',
                alignment: 'left'
            }
        },
        defaultStyle: {
            fontSize: 10,
            alignment: 'left'
        },
        pageMargins: [40, 40, 40, 60]
    };
};

export const gerarPdfDataUrl = async (
    dados: typeEstruturaComparativoInsumos[],
    headerInfo?: HeaderDef
): Promise<string> => {
    if (headerInfo?.logo && headerInfo.logo.startsWith("http")) {
        headerInfo.logo = await fetchImageAsBase64(headerInfo.logo);
    }

    return new Promise((resolve, reject) => {
        try {
            const docDefinition = gerarDocDefinition(dados, headerInfo);
            pdfMake.createPdf(docDefinition).getDataUrl((dataUrl) => {
                resolve(dataUrl);
            });
        } catch (err) {
            reject(err);
        }
    });
};
